import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { Card } from './Card';
import { Skeleton } from './Skeleton';

interface MetricCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  subtitle?: string;
  iconBg?: string;
  loading?: boolean;
  href?: string;
  onClick?: () => void;
  trend?: { value: number; inverted?: boolean };
}

export function MetricCard({
  icon: Icon,
  label,
  value,
  subtitle,
  iconBg = 'bg-primary-100 text-primary-600',
  loading,
  href,
  onClick,
  trend,
}: MetricCardProps) {
  const content = (
    <>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-sm font-medium text-surface-600">{label}</span>
      </div>
      {loading ? (
        <div>
          <Skeleton variant="text" className="w-16 h-7" />
          {subtitle && <Skeleton variant="text" className="w-24 h-3 mt-1" />}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <p className="text-2xl font-bold text-surface-900">{value}</p>
          {trend && trend.value !== 0 && <TrendBadge value={trend.value} inverted={trend.inverted} />}
        </div>
      )}
      {subtitle && <p className="text-xs text-surface-500 mt-1">{subtitle}</p>}
    </>
  );

  if (href) {
    return (
      <Link to={href} className="block group">
        <Card padding="md" className="transition-shadow group-hover:shadow-md group-hover:ring-1 group-hover:ring-primary-200 cursor-pointer">
          {content}
        </Card>
      </Link>
    );
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="block w-full text-left group">
        <Card padding="md" className="transition-shadow group-hover:shadow-md group-hover:ring-1 group-hover:ring-primary-200 cursor-pointer">
          {content}
        </Card>
      </button>
    );
  }

  return (
    <Card padding="md">
      {content}
    </Card>
  );
}

function TrendBadge({ value, inverted }: { value: number; inverted?: boolean }) {
  const isPositive = value > 0;
  // For inverted metrics (like fallback rate), positive change is bad
  const isGood = inverted ? !isPositive : isPositive;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;

  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-full ${
        isGood
          ? 'bg-green-50 text-green-700'
          : 'bg-red-50 text-red-700'
      }`}
    >
      <TrendIcon className="w-3 h-3" />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

export default MetricCard;
